// /employee/customers — Path B customer picker + the portal-invite chase list.
//
// The signed-in employee searches by name / email / company, picks a
// customer, and starts an impersonation session. Selection POSTs to
// /api/employee/impersonation/start which (a) re-verifies the
// permission with Nexus, (b) issues + consumes an internal grant,
// (c) creates the ImpersonationSession, (d) returns an impersonation
// access token + cookie.
//
// The second panel is the invite chase list. The historical migration seeded
// partner accounts that are active and pre-approved but have no password, so
// they cannot log in and nothing was ever sent to them — Nexus's own invite
// only fires for a partner with no account at all. Each company row shows how
// many the company still has and when one was last invited; the per-row action
// in the search results does the same for one person. Sending takes a second
// press, as it does in Nexus, because it mails a real partner.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { PortalCompanyDto } from 'shared';

import { extractApiError } from '../api/client.js';
import { listPortalCompanies } from '../api/employeeJobs.js';
import * as imp from '../api/impersonation.js';
import { useAuth } from '../auth/AuthContext.js';
import { EmployeeSignOut } from '../components/EmployeeSignOut.js';

function invitedLabel(iso: string | null): string {
  if (!iso) return 'Never invited';
  return `Last invited ${new Date(iso).toLocaleDateString()}`;
}

export function EmployeeCustomers(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<imp.PermittedCustomerDto[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [starting, setStarting] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [inviting, setInviting] = useState<string | null>(null);
  // Sending mails a real partner, so it takes a second explicit press — the
  // same posture Nexus's PortalImpersonationButton takes for its one-partner
  // invite, and the sweep mails a whole company at once.
  const [confirming, setConfirming] = useState<string | null>(null);
  const [inviteNotice, setInviteNotice] = useState<string | null>(null);
  const [companies, setCompanies] = useState<PortalCompanyDto[] | null>(null);
  const [companiesError, setCompaniesError] = useState<string | null>(null);

  const hasEmployeeToken = imp.getEmployeeAccessToken() !== null;

  useEffect(() => {
    if (!hasEmployeeToken) return;
    void (async () => {
      try {
        setCompanies(await listPortalCompanies(undefined, { inviteLockedOnly: true }));
      } catch (err) {
        setCompaniesError(
          extractApiError(err)?.message ?? 'Could not load the invite list.'
        );
      }
    })();
  }, [hasEmployeeToken]);

  // If the employee JWT was never issued (e.g. they hit this URL
  // directly without confirming a magic link), bounce them to login.
  if (!hasEmployeeToken) {
    return (
      <div className="card">
        <h2>Sign in required</h2>
        <p>
          Request a magic-link from the <a href="/employee/login">employee sign-in page</a>.
        </p>
      </div>
    );
  }

  const onSearch = (): void => {
    if (q.trim().length === 0) return;
    setSearching(true);
    setErrorMessage(null);
    void (async () => {
      try {
        const rows = await imp.searchEmployeeCustomers(q.trim());
        setResults(rows);
      } catch (err) {
        setErrorMessage(extractApiError(err)?.message ?? 'Customer search failed.');
      } finally {
        setSearching(false);
      }
    })();
  };

  // Keyed by ROW, not by account: one account can hold several rows (one per
  // company), so an account-keyed busy flag lights every one of them at once.
  const rowKey = (c: imp.PermittedCustomerDto): string =>
    `${c.company_id ?? 'no-company'}:${c.customer_user_id}`;

  const onPick = (c: imp.PermittedCustomerDto): void => {
    setStarting(rowKey(c));
    setErrorMessage(null);
    void (async () => {
      try {
        await auth.startImpersonationAsEmployee(c.customer_user_id, c.company_id);
        navigate('/quotes', { replace: true });
      } catch (err) {
        setErrorMessage(
          extractApiError(err)?.message ?? 'Could not start the impersonation session.'
        );
      } finally {
        setStarting(null);
      }
    })();
  };

  const onInvite = (customer: imp.PermittedCustomerDto): void => {
    setInviting(customer.customer_user_id);
    setConfirming(null);
    setErrorMessage(null);
    setInviteNotice(null);
    void (async () => {
      try {
        const result = await imp.invitePortalAccount(customer.customer_user_id);
        setInviteNotice(`Invite sent to ${result.email}.`);
        setResults((rows) =>
          rows?.map((r) =>
            r.customer_user_id === customer.customer_user_id
              ? { ...r, last_invited_at: result.last_invited_at }
              : r
          ) ?? rows
        );
        // The two panels are independently fetched, so a per-row invite has to
        // move the company badge too or the chase list reads stale.
        setCompanies(await listPortalCompanies(undefined, { inviteLockedOnly: true }));
      } catch (err) {
        setErrorMessage(extractApiError(err)?.message ?? 'Could not send the invite.');
      } finally {
        setInviting(null);
      }
    })();
  };

  const onInviteCompany = (company: PortalCompanyDto): void => {
    setInviting(company.company_id);
    setConfirming(null);
    setCompaniesError(null);
    setInviteNotice(null);
    void (async () => {
      try {
        const result = await imp.inviteCompanyPortalAccounts(company.company_id);
        const parts = [`${result.sent} invite${result.sent === 1 ? '' : 's'} sent`];
        if (result.skipped_recently_invited > 0) {
          parts.push(`${result.skipped_recently_invited} skipped (invited recently)`);
        }
        if (result.failed > 0) parts.push(`${result.failed} could not be sent`);
        setInviteNotice(
          `${company.company_name ?? company.company_id}: ${parts.join(', ')}.`
        );
        setCompanies(await listPortalCompanies(undefined, { inviteLockedOnly: true }));
        // Same drift in the other direction: a row in an open search result may
        // have just been swept, so carry the new timestamp onto it.
        const invited = new Map(result.results.map((r) => [r.customer_user_id, r]));
        setResults((rows) =>
          rows?.map((r) => {
            const hit = invited.get(r.customer_user_id);
            return hit && hit.outcome === 'sent'
              ? { ...r, last_invited_at: hit.last_invited_at }
              : r;
          }) ?? rows
        );
      } catch (err) {
        setCompaniesError(
          extractApiError(err)?.message ?? 'Could not send the company invites.'
        );
      } finally {
        setInviting(null);
      }
    })();
  };

  const totalInviteLocked = (companies ?? []).reduce(
    (sum, c) => sum + c.invite_locked_count,
    0
  );

  return (
    <>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <h2>Pick a customer to assist</h2>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, fontSize: 14 }}>
            <Link to="/employee/jobs">Print jobs →</Link>
            <EmployeeSignOut />
          </div>
        </div>
        <p style={{ color: 'var(--color-text-muted)' }}>
          Search the customers you're permitted to view in Nexus. Selecting one starts an audited impersonation session.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSearch();
          }}
          style={{ display: 'flex', gap: 8, marginTop: 16 }}
        >
          <input
            type="text"
            required
            minLength={1}
            maxLength={128}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Company name, contact email, etc."
            style={{ flex: 1 }}
          />
          <button className="primary" type="submit" disabled={searching}>
            {searching ? 'Searching…' : 'Search'}
          </button>
        </form>
        {errorMessage && (
          <div className="banner banner--error" style={{ marginTop: 16 }}>
            {errorMessage}
          </div>
        )}
        {inviteNotice && (
          <div className="banner banner--success" style={{ marginTop: 16 }}>
            {inviteNotice}
          </div>
        )}
        {results && results.length === 0 && (
          <p style={{ marginTop: 16 }}>No matches.</p>
        )}
        {results && results.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, marginTop: 16 }}>
            {results.map((c) => (
              <li
                key={rowKey(c)}
                style={{
                  padding: '10px 12px',
                  borderTop: '1px solid var(--color-border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div>
                  {/* Nexus composes display_name from the company and contact
                      names and sends null when it has neither, so the row falls
                      back rather than rendering a blank heading. */}
                  <div style={{ fontWeight: 600 }}>
                    {c.display_name ?? c.company_name ?? c.primary_email}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                    {c.primary_email}
                    {c.company_name ? ` · ${c.company_name}` : ''}
                  </div>
                  {c.invite_locked && (
                    <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                      Cannot sign in yet · {invitedLabel(c.last_invited_at)}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {c.invite_locked &&
                    (confirming === c.customer_user_id ? (
                      <>
                        <button type="button" onClick={() => setConfirming(null)}>
                          Cancel
                        </button>
                        <button
                          className="primary"
                          type="button"
                          disabled={inviting !== null}
                          onClick={() => onInvite(c)}
                        >
                          Email {c.primary_email}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        disabled={inviting !== null}
                        onClick={() => setConfirming(c.customer_user_id)}
                      >
                        {inviting === c.customer_user_id
                          ? 'Sending…'
                          : c.last_invited_at
                            ? 'Resend invite'
                            : 'Send invite'}
                      </button>
                    ))}
                  <button
                    type="button"
                    disabled={starting !== null}
                    onClick={() => onPick(c)}
                  >
                    {starting === rowKey(c) ? 'Starting…' : 'Impersonate'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <h2>Waiting on a portal invite</h2>
          {companies !== null && (
            <span style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>
              {totalInviteLocked} account{totalInviteLocked === 1 ? '' : 's'} ·{' '}
              {companies.length} compan{companies.length === 1 ? 'y' : 'ies'}
            </span>
          )}
        </div>
        <p style={{ color: 'var(--color-text-muted)' }}>
          These accounts are set up and approved but have never had a password, so they
          cannot sign in. Sending an invite emails them a link to choose one.
        </p>
        {companiesError && (
          <div className="banner banner--error" style={{ marginTop: 16 }}>
            {companiesError}
          </div>
        )}
        {companies === null && !companiesError && <p style={{ marginTop: 16 }}>Loading…</p>}
        {companies !== null && companies.length === 0 && (
          <p style={{ marginTop: 16 }}>Everyone has been invited.</p>
        )}
        {companies !== null && companies.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, marginTop: 16 }}>
            {companies.map((c) => (
              <li
                key={c.company_id}
                style={{
                  padding: '10px 12px',
                  borderTop: '1px solid var(--color-border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{c.company_name ?? c.company_id}</div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                    {c.invite_locked_count} waiting · {invitedLabel(c.invite_last_sent_at)}
                  </div>
                </div>
                {confirming === c.company_id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button type="button" onClick={() => setConfirming(null)}>
                      Cancel
                    </button>
                    <button
                      className="primary"
                      type="button"
                      disabled={inviting !== null}
                      onClick={() => onInviteCompany(c)}
                    >
                      Email {c.invite_locked_count} partner
                      {c.invite_locked_count === 1 ? '' : 's'}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={inviting !== null}
                    onClick={() => setConfirming(c.company_id)}
                  >
                    {inviting === c.company_id
                      ? 'Sending…'
                      : `Invite all (${c.invite_locked_count})`}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
